import type { RunCompletion } from "@/redux/reel-runs.reducer"
import type { NoChildren } from "@/theme"
import type { DimensionValue, ViewProps } from "react-native"

import { isNumberValid } from "@/base/type-guards"
import { generateRunProgressFractionText } from "@/redux/reel-runs.reducer"
import { useMeasurementPreference } from "@/redux/selectors"
import { AppText, Theme, useIsDarkMode } from "@/theme"
import { useTranslation } from "react-i18next"
import { StyleSheet, View } from "react-native"
import { useStyle } from "react-native-style-utilities"

const styles = StyleSheet.create({
  barFill: {
    backgroundColor: Theme.COLORS.$blue[300],
    borderBottomLeftRadius: Theme.RADII.$md,
    borderTopLeftRadius: Theme.RADII.$md,
    bottom: 0,
    left: 0,
    position: "absolute",
    top: 0,
  },
  progressBar: {
    alignItems: "center",
    backgroundColor: Theme.COLORS.$gray[300],
    borderBottomLeftRadius: Theme.RADII.$md,
    borderTopLeftRadius: Theme.RADII.$md,
    display: "flex",
    flexDirection: "row",

    justifyContent: "space-between",
    maxHeight: Theme.SIZES.$9,
    minWidth: 50,
    paddingHorizontal: Theme.SPACING.$2,
    position: "relative",
  },
  progressBarDark: {
    backgroundColor: Theme.COLORS.$gray[500],
  },
  textInner: {
    color: Theme.COLORS.$textDark.primary,
    fontSize: Theme.FONT_SIZES.$md,
    marginVertical: "auto",
    zIndex: 1,
  },
  textLeftContainer: {
    marginRight: Theme.SPACING.$4,
  },
})

export function ProgressBar({
  barFillColor,
  fillPercentage,
  style,
  textLeft,
  textRight,
  ...rest
}: NoChildren<ViewProps> & {
  barFillColor?: string
  fillPercentage: number
  textLeft?: string
  textRight?: string
}) {
  const isDark = useIsDarkMode()

  const right: DimensionValue = `${100 - fillPercentage}%`
  if (!Boolean(barFillColor)) {
    barFillColor = Theme.COLORS.$blue[300]
  }
  return (
    <View
      style={useStyle(() => {
        return [
          styles.progressBar,
          isDark ? styles.progressBarDark : undefined,
          style,
        ]
      }, [isDark, style])}
      {...rest}
    >
      <View
        style={[
          styles.barFill,
          {
            backgroundColor: barFillColor,
            right,
          },
        ]}
      />
      <View style={styles.textLeftContainer}>
        <AppText style={styles.textInner}>{textLeft}</AppText>
      </View>
      <AppText style={styles.textInner}>{textRight}</AppText>
    </View>
  )
}

/**
 * A progress bar that shows the completion percentage of a run.
 */
export function RunProgressBar({
  runCompletionPct,
  runDistanceMmElapsed,
  runDistanceMmMax,
  ...rest
}: RunCompletion & ViewProps) {
  const { t } = useTranslation()
  const measurementPreference = useMeasurementPreference()

  if (isNumberValid(runCompletionPct) && runDistanceMmMax > 0) {
    return (
      <ProgressBar
        {...rest}
        fillPercentage={runCompletionPct}
        textLeft={t("percentWithVal", { val: runCompletionPct })}
        textRight={generateRunProgressFractionText({
          measurementPreference,
          runDistanceMmElapsed,
          runDistanceMmMax,
        })}
      />
    )
  }

  return null
}
